import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { ChevronDown, ChevronUp, ExternalLink, Users, Maximize2, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TIMEFRAME_OPTIONS = [
  { value: "1", label: "1M" },
  { value: "3", label: "3M" },
  { value: "6", label: "6M" },
  { value: "12", label: "12M" },
  { value: "24", label: "24M" },
];
const TIMEFRAME_STORAGE_KEY = "zzx.insider.timeframe.months";

function fmtNum(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n}`;
}
function fmtMoney(n) {
  if (n == null) return "—";
  return `$${fmtNum(n)}`;
}
// Compact value formatter used in the summary Value column — no decimal places.
function fmtMoneyCompact(n) {
  if (n == null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${Math.round(n / 1e9)}B`;
  if (abs >= 1e6) return `$${Math.round(n / 1e6)}M`;
  if (abs >= 1e3) return `$${Math.round(n / 1e3)}K`;
  return `$${Math.round(n)}`;
}
function fmtPrice(n) {
  if (n == null || !isFinite(n)) return "—";
  return `$${n.toFixed(2)}`;
}
function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d)) return s;
  // Compact numeric M/D/YY (e.g. 7/1/26)
  return `${d.getMonth() + 1}/${d.getDate()}/${String(d.getFullYear()).slice(-2)}`;
}

function classify(txn) {
  if (!txn) return "other";
  if (/buy|purchase|acquisit/i.test(txn)) return "buy";
  if (/sale|sell|dispos/i.test(txn)) return "sell";
  return "other";
}

export default function InsiderSummary({ ticker }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [months, setMonths] = useState(() => {
    try { return localStorage.getItem(TIMEFRAME_STORAGE_KEY) || "12"; } catch { return "12"; }
  });

  const setMonthsPersist = (v) => {
    setMonths(v);
    try { localStorage.setItem(TIMEFRAME_STORAGE_KEY, v); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!ticker) return;
    fetchInsiders();
    // eslint-disable-next-line
  }, [ticker]);

  const fetchInsiders = async () => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/market/insiders/${ticker}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  const allRows = data?.transactions || [];

  // Filter by selected timeframe (in months) + only buys/sells for the compact summary
  const summaryRows = useMemo(() => {
    const m = parseInt(months, 10) || 12;
    const cutoff = Date.now() - m * 30 * 24 * 3600 * 1000;
    return allRows
      .map((r) => ({ ...r, kind: classify(r.transaction) }))
      .filter((r) => r.kind === "buy" || r.kind === "sell")
      .filter((r) => {
        if (!r.date) return false;
        const d = new Date(r.date).getTime();
        return !isNaN(d) && d >= cutoff;
      });
  }, [allRows, months]);

  const buys = summaryRows.filter((r) => r.kind === "buy").length;
  const sells = summaryRows.filter((r) => r.kind === "sell").length;
  const monthsLabel = TIMEFRAME_OPTIONS.find((o) => o.value === months)?.label || `${months}M`;

  return (
    <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm" data-testid="insider-summary">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900">
        <button
          onClick={() => setCollapsed((v) => !v)}
          data-testid="insider-summary-toggle"
          className="flex items-center gap-2 text-zinc-400 hover:text-zinc-200"
        >
          <Users className="w-4 h-4 text-blue-400" />
          <h3 className="font-heading text-sm uppercase tracking-wider">Insider Summary</h3>
          {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
        </button>
        <div className="flex items-center gap-1">
          <Select value={months} onValueChange={setMonthsPersist}>
            <SelectTrigger
              data-testid="insider-timeframe-select"
              onClick={(e) => e.stopPropagation()}
              className="h-6 w-[68px] bg-zinc-950 border-zinc-800 rounded-sm font-mono text-[10px] uppercase tracking-wider text-zinc-300 px-2"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 rounded-sm text-xs">
              {TIMEFRAME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} data-testid={`insider-timeframe-${o.label}`}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={fetchInsiders}
            disabled={loading}
            data-testid="insider-regenerate"
            title="Refresh insider filings"
            className="p-1 text-zinc-500 hover:text-blue-400 disabled:opacity-30"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={() => setDetailOpen(true)}
            disabled={loading || allRows.length === 0}
            data-testid="insider-expand-btn"
            title="View all filings"
            className="p-1 text-zinc-500 hover:text-blue-400 disabled:opacity-30"
          >
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="px-3 py-2">
          {loading && <div className="text-sm text-zinc-500 font-mono py-2">Loading…</div>}
          {error && <div className="text-sm text-rose-500 font-mono py-2" data-testid="insider-summary-error">{error}</div>}
          {!loading && !error && (
            <>
              <div className="text-xs font-mono text-zinc-500 mb-2">
                Last {monthsLabel} · <span className="text-emerald-500">{buys} buy</span> · <span className="text-rose-500">{sells} sell</span>
              </div>
              {summaryRows.length === 0 ? (
                <div className="text-sm text-zinc-600 py-1">No buys or sells reported.</div>
              ) : (
                <table className="w-full text-sm" data-testid="insider-summary-table">
                  <thead>
                    <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                      <th className="text-left font-mono py-1 font-normal">Date</th>
                      <th className="text-left font-mono py-1 font-normal">B/S</th>
                      <th className="text-right font-mono py-1 font-normal">Shares</th>
                      <th className="text-right font-mono py-1 font-normal">Price</th>
                      <th className="text-right font-mono py-1 font-normal">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.slice(0, 12).map((r, i) => {
                      const tone = r.kind === "buy" ? "text-emerald-500" : "text-rose-500";
                      const price = r.shares && r.value ? r.value / r.shares : null;
                      return (
                        <tr key={i} className="border-b border-zinc-900/60" data-testid={`insider-summary-row-${i}`}>
                          <td className="py-1 font-mono text-zinc-400 whitespace-nowrap">{fmtDate(r.date)}</td>
                          <td className={`py-1 font-mono font-semibold ${tone}`}>{r.kind === "buy" ? "B" : "S"}</td>
                          <td className="py-1 font-mono text-right text-zinc-300">{fmtNum(r.shares)}</td>
                          <td className="py-1 font-mono text-right text-zinc-300">{fmtPrice(price)}</td>
                          <td className={`py-1 font-mono text-right ${tone}`}>{fmtMoneyCompact(r.value)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {summaryRows.length > 12 && (
                <button onClick={() => setDetailOpen(true)} className="mt-2 text-xs font-mono uppercase tracking-wider text-blue-400 hover:text-blue-300" data-testid="insider-view-more">
                  +{summaryRows.length - 12} more · view all
                </button>
              )}
            </>
          )}
        </div>
      )}

      <InsiderDetailDialog open={detailOpen} onOpenChange={setDetailOpen} ticker={ticker} rows={allRows} />
    </div>
  );
}

function InsiderDetailDialog({ open, onOpenChange, ticker, rows }) {
  const buys = rows.filter((r) => classify(r.transaction) === "buy").length;
  const sells = rows.filter((r) => classify(r.transaction) === "sell").length;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="insider-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-400" />
            {ticker} · Insider Filings
            <span className="text-[10px] font-mono text-zinc-500 ml-2">
              {rows.length} filings · <span className="text-emerald-500">{buys} buy</span> · <span className="text-rose-500">{sells} sell</span>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto -mx-6 px-6 mt-3">
          {rows.length === 0 ? (
            <div className="text-sm text-zinc-500 py-6">No insider filings found.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#0a0a0a]">
                <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-900">
                  <th className="text-left font-mono px-2 py-2 font-normal">Date</th>
                  <th className="text-left font-mono px-2 py-2 font-normal">Insider</th>
                  <th className="text-left font-mono px-2 py-2 font-normal">Transaction</th>
                  <th className="text-right font-mono px-2 py-2 font-normal">Shares</th>
                  <th className="text-right font-mono px-2 py-2 font-normal">Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const kind = classify(r.transaction);
                  const tone = kind === "buy" ? "text-emerald-500" : kind === "sell" ? "text-rose-500" : "text-zinc-300";
                  return (
                    <tr key={i} className="border-b border-zinc-900/60 hover:bg-zinc-900/40" data-testid={`insider-detail-row-${i}`}>
                      <td className="px-2 py-1.5 font-mono text-zinc-400 whitespace-nowrap">{fmtDate(r.date)}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-zinc-200">{r.insider || "—"}</div>
                        {r.position && <div className="text-[10px] text-zinc-600">{r.position}</div>}
                      </td>
                      <td className={`px-2 py-1.5 font-mono ${tone}`}>{r.transaction || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-right text-zinc-300">{fmtNum(r.shares)}</td>
                      <td className={`px-2 py-1.5 font-mono text-right ${tone}`}>
                        {r.url ? (
                          <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline">
                            {fmtMoney(r.value)}<ExternalLink className="w-3 h-3 opacity-60" />
                          </a>
                        ) : fmtMoney(r.value)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
